import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRootFolderContents, getFolderContents } from '@/api/document.api';
import { errorHandler } from '@/utils/toastHandler';
import PageContainer from '@/components/Container/PageContainer';
import DocumentList from '@/components/DocLibrary/DocumentList';
import { DropZone, UploadFileList } from '@/components/UI/FileDropzone';
import { Flex, LoadingPage } from '@/layout';
import { Button } from '@/components/UI/Button';
import Loading from '@/components/UI/Loading';
import { StorageBar } from '@/components/UI/StorageBar';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { Folder } from '@/components/Icon';
import { useUploadBatch } from './useUploadBatch';
import { getUploadSelectionError } from './uploadSelection';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';

const DocLibrary = () => {
  // @ts-ignore
  const { t } = useTranslation();

  const [isDropActive, setIsDropActive] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const { userData } = useAppSelector(getAuthSelector);
  const uploads = useUploadBatch(String(userData?.id || 'guest'));
  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [storageRefreshKey, setStorageRefreshKey] = useState(0);
  const [showDocumentStorageAlmostFullModal, setShowDocumentStorageAlmostFullModal] = useState<boolean>(false);
  const [showDocumentStorageFullModal, setShowDocumentStorageFullModal] = useState<boolean>(false);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [previousFolderId, setPreviousFolderId] = useState<number | null>(null);
  const [breadCrumbList, setBreadcrumbList] = useState<{ id: number | null; name: string }[]>([{ id: null, name: 'Root' }]);

  const onDragStateChange = useCallback((dragActive: boolean) => {
    setIsDropActive(dragActive);
  }, []);

  const onFilesDrop = useCallback((files: File[]) => {
    setFiles(files);
  }, []);

  const onUploadFile = async () => {
    if (uploads.busy || !files.length) return;
    const selectionError = getUploadSelectionError(files);
    if (selectionError) {
      errorHandler(t(`pages.docLibrary.${selectionError}`)); return;
    }
    await uploads.start(files, currentFolderId);
    setFiles([]);
    await refreshDocuments(currentFolderId);
  };

  const getDocuments = useCallback(async (id: number | null, propagateError = false) => {
    setIsLoading(true);
    try {
      // 根據有無設定 currentFolderId 來決定要撈根目錄或是特定資料夾內容
      if (id) {
        const res = (await getFolderContents(id)).data;
        if (!res.success) throw new Error('Document list refresh failed');
        setFileList(res.data);
      }
      if (!id) {
        const res = (await getRootFolderContents()).data;
        if (!res.success) throw new Error('Document list refresh failed');
        setFileList(res.data);
      }
    } catch (error) {
      if (propagateError) throw error;
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshDocuments = async (id: number | null, propagateError = false) => {
    await getDocuments(id, propagateError);
    setStorageRefreshKey((current) => current + 1);
  };

  useEffect(() => {
    // 初始化不加入參數, 代表撈根目錄
    getDocuments(null);
  }, [getDocuments]);

  const updateBreadcrumb = (folderId: number | null, name: string) => {
    if (folderId === null) {
      setBreadcrumbList([{ id: null, name: 'Root' }]);
    } else {
      setBreadcrumbList((prev) => {
        // 如果 folderId 已存在於麵包屑，表示要回到該層（返回上一層或中間層）
        const existIndex = prev.findIndex(item => item.id === folderId);
        if (existIndex !== -1) {
          return prev.slice(0, existIndex + 1);
        }
        // 否則往下 push
        return [...prev, { id: folderId, name }];
      });
    }
  }

  return (
    <PageContainer title={t('docLibrary')}>
      <LoadingPage isLoading={isLoading} />
      <div style={{ width: '100%', maxWidth: '1200px', background: '#ffffff', padding: '30px 40px', borderRadius: 15 }}>
        <StorageBar
          refreshKey={storageRefreshKey}
          showStorageAlmostFullModal={showDocumentStorageAlmostFullModal}
          showStorageFullModal={showDocumentStorageFullModal}
          setShowStorageAlmostFullModal={setShowDocumentStorageAlmostFullModal}
          setShowStorageFullModal={setShowDocumentStorageFullModal}
        />
        {uploads.items.length > 0 && <section aria-label={t('pages.docLibrary.batchResults')}>
          <h2>{t('pages.docLibrary.batchResults')}</h2>
          {uploads.storageError && <p role="alert">{t('pages.docLibrary.uploadStorageUnavailable')}</p>}
          <ul>{uploads.items.map((item) => <li key={item.key}>
            <span>{item.name} — {t(`pages.docLibrary.uploadStates.${item.status}` as any)}</span>
            {item.error && <span role="status"> — {t(`pages.docLibrary.uploadErrors.${item.error}` as any)}</span>}
            {item.status === 'unknown' && <Button type="button" disabled={uploads.busy} onClick={async () => { await uploads.check(item); await refreshDocuments(currentFolderId); }}>{t('pages.docLibrary.checkUpload')}</Button>}
            {item.status === 'failed' && (item.file
              ? <Button type="button" disabled={uploads.busy} onClick={async () => { await uploads.retry(item); await refreshDocuments(currentFolderId); }}>{t('pages.docLibrary.retryUpload')}</Button>
              : <label>{t('pages.docLibrary.reselectUpload')}<input type="file" disabled={uploads.busy} onChange={async (event) => {
                  const file = event.target.files?.[0]; event.target.value = '';
                  if (file) { await uploads.retry(item, file); await refreshDocuments(currentFolderId); }
                }} /></label>)}
          </li>)}</ul>
        </section>}

        <DropZone multiple disabled={uploads.busy} files={files} onDragStateChange={onDragStateChange} onFilesDrop={onFilesDrop} setFiles={setFiles} />
        <Text color={theme.colors.gray400} fontSize={theme.fontSizes.sm} textAlign={'center'}>
          {t('pattern.documentFileName')}
        </Text>
        <Flex width={'100%'} justifyContent={'center'} alignItems={'center'} gridGap={'15px'} marginBottom={'20px'}>
          {files.length === 0 ? (
            <p>{t('noFileToUpload')}</p>
          ) : (
            <p>
              {t('filesReadyToUpload')}: {files.length}
            </p>
          )}
          <UploadFileList files={files} />
          {files.length > 0 && (
            <Button type="button" disabled={isLoading || uploads.busy} size="table" variant={'danger'} onClick={onUploadFile}>
              {t('buttons.upload')}
            </Button>
          )}
        </Flex>
        <Flex alignItems={'center'} gridGap={'10px'} flexWrap={'wrap'} >
          {
            breadCrumbList.map((item, index) => (
              <Flex gridGap={'5px'} key={item.id || 'root'}>
                <Text lineHeight={'28px'}>
                  {index !== 0 && ' / '}
                </Text>
                <Folder.FolderSVG />
                <div
                  style={{ cursor: 'pointer', color: item.id === currentFolderId ? theme.colors.primary500 : theme.colors.primary100, fontWeight: 'bold', lineHeight: '28px' }}
                  onClick={() => {
                    // 若最後一個元素則不執行
                    if (index === breadCrumbList.length - 1) return;
                    getDocuments(item.id);
                    updateBreadcrumb(item.id, item.name);
                    setCurrentFolderId(item.id);
                  }}
                >
                  {item.id === null ? t('pages.docLibrary.root') : item.name}
                </div>
              </Flex>
            ))
          }
        </Flex>
        {fileList ? <DocumentList breadCrumbList={breadCrumbList} data={fileList} getDocuments={refreshDocuments} currentFolderId={currentFolderId} previousFolderId={previousFolderId} setCurrentFolderId={setCurrentFolderId} setPreviousFolderId={setPreviousFolderId} updateBreadcrumb={updateBreadcrumb} /> : <Loading />}
      </div>
    </PageContainer>
  );
};

export default DocLibrary;
